/**
 * Environment variable helper
 * Works in both Vite (browser) and Jest (test) environments
 */

// Type for valid runtime config keys
type RuntimeConfigKey = keyof NonNullable<RuntimeConfig>;

const buildTimeEnv: Record<string, string | undefined> =
  typeof __VITE_ENV__ !== 'undefined'
    ? (__VITE_ENV__ as Record<string, string | undefined>)
    : {};

/**
 * Get environment variable with fallback
 * Priority: Runtime Config > Build-time Env Vars > Fallback
 */
export const getEnvVar = (key: string, fallback: string): string => {
  // 1. Check runtime config first (from config.js)
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    const runtimeValue = window.__RUNTIME_CONFIG__[key as RuntimeConfigKey];
    // Make sure it's not still a template placeholder AND not empty
    if (
      runtimeValue &&
      runtimeValue !== `\${${key}}` &&
      runtimeValue.trim() !== ''
    ) {
      return runtimeValue;
    }
  }

  // 2. Check Vite/rsbuild build-time environment variables
  const buildTimeValue = buildTimeEnv[key];
  if (buildTimeValue !== undefined && buildTimeValue !== '') {
    return String(buildTimeValue);
  }

  // 3. Fallback for tests and when neither is available
  return fallback;
};

export const getNumericEnvVar = (key: string, fallback: number): number => {
  const parsed = Number(getEnvVar(key, String(fallback)));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

/**
 * Environment configuration
 * Centralized access to all environment variables
 */
export const env = {
  // Backend API Configuration
  PERIVERSE_API_URL: getEnvVar(
    'VITE_PERIVERSE_API_URL',
    'http://localhost:3000'
  ),

  // Auth API Configuration
  PERIVERSE_AUTH_API_URL: getEnvVar(
    'VITE_PERIVERSE_AUTH_API_URL',
    'http://localhost:3000'
  ),

  // Frontend Configuration
  FRONTEND_URL: getEnvVar('VITE_FRONTEND_URL', 'http://localhost:4200'),
  REDIRECT_URI: getEnvVar(
    'VITE_REDIRECT_URI',
    'http://localhost:4200/auth/callback'
  ),

  // Development Configuration
  DEV_MODE: getEnvVar('VITE_DEV_MODE', 'true') === 'true',
  DEBUG_AUTH: getEnvVar('VITE_DEBUG_AUTH', 'false') === 'true',
  SHOW_TRACK_ASSET_DEV_TOGGLE:
    getEnvVar('VITE_SHOW_TRACK_ASSET_DEV_TOGGLE', 'false') === 'true',

  STAGING_BUFFER_MINUTES: getNumericEnvVar('VITE_STAGING_BUFFER_MINUTES', 60),
  AT_RISK_BUFFER_MINUTES: getNumericEnvVar('VITE_AT_RISK_BUFFER_MINUTES', 30),
} as const;

// Log loaded config in development
if (typeof window !== 'undefined' && env.DEV_MODE) {
  console.log('🔧 Environment config loaded:', env);
}
